import { ParseResult } from 'papaparse';
import {
  EXPECTED_COLUMN_FIELDS,
  MATCH_THRESHOLD,
  MIN_LOADING_MS,
  REQUIRED_COLUMN_GROUPS,
} from './constants';
import {
  ColumnKey,
  ColumnMatchField,
  ColumnMatchingStatus,
  FileMatches,
  NormalizeFile,
} from './types';

export const await5Seconds = () =>
  new Promise((resolve) => {
    setTimeout(resolve, MIN_LOADING_MS);
  });

export function normalizeCSVData(
  papaResult: ParseResult<unknown>,
): NormalizeFile {
  if (
    Array.isArray(papaResult.data) &&
    typeof papaResult.data[0] === 'object' &&
    !Array.isArray(papaResult.data[0])
  ) {
    // header: true
    const headers =
      papaResult.meta?.fields || Object.keys(papaResult.data[0] || {});
    const rows = papaResult.data.map((row) =>
      headers.map((h) => (row as Record<string, unknown>)[h] ?? ''),
    );
    return { headers, rows: rows as string[][] };
  } else if (
    Array.isArray(papaResult.data) &&
    Array.isArray(papaResult.data[0])
  ) {
    // header: false
    const [headers, ...rows] = papaResult.data;
    return { headers, rows: rows as string[][] };
  }
  throw new Error('Unknown PapaParse data structure');
}

function findRegexMatch(
  field: ColumnMatchField,
  colIdx: number,
  rows: string[][],
) {
  let regexes: RegExp[];
  if (field.valueRegex) {
    if (Array.isArray(field.valueRegex)) {
      regexes = field.valueRegex;
    } else {
      regexes = [field.valueRegex];
    }
  } else {
    regexes = [];
  }

  if (regexes.length > 0) {
    const colValues = rows?.map((row) => row[colIdx]).filter(Boolean) ?? [];
    const matches = colValues.filter((val) =>
      regexes.some((re) => re.test(val)),
    );
    if (
      matches.length > 0 &&
      matches.length >= Math.floor(colValues.length * MATCH_THRESHOLD)
    ) {
      return true;
    }
  }

  return false;
}

function findAllowedValuesMatch(
  field: ColumnMatchField,
  colIdx: number,
  rows: string[][],
) {
  if (field.allowedValues && Array.isArray(field.allowedValues)) {
    const colValues =
      rows?.map((row) => String(row[colIdx] || '').toLowerCase()) ?? [];
    const matches = colValues.filter((val) =>
      field.allowedValues?.includes(val),
    );
    if (
      matches.length > 0 &&
      matches.length >= Math.floor(colValues.length * MATCH_THRESHOLD)
    ) {
      return true;
    }
  }

  return false;
}

export function autoMatchColumns({
  headers,
  rows,
}: {
  headers?: string[];
  rows: string[][];
}) {
  const usedFields = new Set();
  const columnCount = headers?.length || rows[0]?.length || 0;

  const matches = Array.from({ length: columnCount }).map((_, colIdx) => {
    let matchField = null;
    const matchedBy: ColumnMatchingStatus = ColumnMatchingStatus.SYSTEM_MATCHED;

    // Header match
    if (headers && headers.length > colIdx) {
      const normalizedHeader = headers[colIdx].trim().toLowerCase();
      matchField = EXPECTED_COLUMN_FIELDS.find(
        ({ labels, key }) =>
          !usedFields.has(key) &&
          labels.some(
            (label) => normalizedHeader === label.trim().toLowerCase(),
          ),
      );
    }

    // Regex match
    if (!matchField) {
      for (const field of EXPECTED_COLUMN_FIELDS) {
        if (!usedFields.has(field.key)) {
          const regexMatch = findRegexMatch(
            field as ColumnMatchField,
            colIdx,
            rows,
          );
          if (regexMatch) {
            matchField = field;
            break;
          }
        }
      }
    }

    // Allowed values match
    if (!matchField) {
      for (const field of EXPECTED_COLUMN_FIELDS) {
        if (!usedFields.has(field.key)) {
          const allowedValuesMatch = findAllowedValuesMatch(
            field as ColumnMatchField,
            colIdx,
            rows,
          );
          if (allowedValuesMatch) {
            matchField = field;
            break;
          }
        }
      }
    }

    if (matchField) {
      usedFields.add(matchField.key);
      return {
        field: matchField.key,
        matched: true,
        matchedBy,
        columnIndex: colIdx,
        columnHeader: headers ? headers[colIdx] : null,
        required: matchField.required || false,
        recommended: matchField.recommended || false,
        values: rows?.map((row) => row[colIdx]) || [],
        errorType: undefined,
        errorMsg: undefined,
        groupMatches: null,
      };
    }

    // Not matched
    return {
      field: null,
      matched: false,
      matchedBy: null,
      columnIndex: colIdx,
      columnHeader: headers ? headers[colIdx] : null,
      required: false,
      recommended: false,
      values: rows?.map((row) => row[colIdx]) || [],
      errorType: undefined,
      errorMsg: undefined,
      groupMatches: undefined,
    };
  });

  const groupMatches = REQUIRED_COLUMN_GROUPS.map((group) => {
    const matchedGroupFields = matches.filter(
      (m) => m.matched && group.keys.includes(m.field as ColumnKey),
    );

    return {
      field: group.groupKey,
      matched: matchedGroupFields.length > 0,
      matchedBy: ColumnMatchingStatus.GROUP,
      required: group.required,
      recommended: group.recommended,
      label: group.label,
      groupMatches: matchedGroupFields.map((m) => ({
        field: m.field || null,
        columnIndex: m.columnIndex || null,
        columnHeader: m.columnHeader || '',
        values: m.values || [],
        matched: m.matched || false,
        matchedBy: m.matchedBy || '',
        required: m.required || false,
        recommended: m.recommended || false,
      })),
      columnIndex: null,
      columnHeader: null,
      values: matchedGroupFields.map((m) => m.values).flat(),
    };
  });

  return [...matches, ...groupMatches];
}

export function matchedFields(
  formattedFile: NormalizeFile | null,
): FileMatches {
  if (!formattedFile) {
    return [];
  }
  const matched = autoMatchColumns({
    headers: formattedFile.headers,
    rows: formattedFile.rows || [],
  }).map((m) => {
    const isValid = validateColumnsValues(
      (m.field as ColumnKey) || ColumnKey.Unmatched,
      m.values,
    );

    return {
      ...m,
      field: m.field as ColumnKey,
      matchedBy: m.matchedBy || ColumnMatchingStatus.UNMATCHED,
      columnIndex: m.columnIndex,
      columnHeader: m.columnHeader || '',
      required: m.required || false,
      recommended: m.recommended || false,
      values: m.values || [],
      errorType: isValid ? undefined : 'format',
      errorMsg: isValid ? undefined : 'Formatting error',
      groupMatches: m.groupMatches || undefined,
    };
  });

  return matched as FileMatches;
}

export function validateRowsValuesByField(
  fieldKey: ColumnKey,
  value: string,
): { valid: boolean } {
  const fieldConfig = EXPECTED_COLUMN_FIELDS.find((f) => f.key === fieldKey);

  if (!value || value.trim() === '') {
    return { valid: !fieldConfig?.required };
  }
  if (!fieldConfig) {
    return { valid: true };
  }

  if (fieldConfig.allowedValues && Array.isArray(fieldConfig.allowedValues)) {
    const normalized = (value || '').toLowerCase();
    if (!fieldConfig.allowedValues.includes(normalized)) {
      return {
        valid: false,
      };
    }
    return { valid: true };
  }

  if (fieldConfig.valueRegex) {
    const regexes = Array.isArray(fieldConfig.valueRegex)
      ? fieldConfig.valueRegex
      : [fieldConfig.valueRegex];

    const matchesAny = regexes.some((re) => re.test(value));
    if (!matchesAny) {
      return {
        valid: false,
      };
    }
    return { valid: true };
  }

  return { valid: true };
}

export function getFileMatchesByGroup(
  fileMatches: FileMatches,
  emailFields: ColumnKey[],
): FileMatches {
  const hasAnyEmailMatched = fileMatches.some(
    (f) => emailFields.includes(f.field as ColumnKey) && f.matched,
  );

  const groupIndex = fileMatches.findIndex((f) => f.matchedBy === 'group');

  if (groupIndex === -1) return fileMatches;

  const updatedGroup = {
    ...fileMatches[groupIndex],
    matched: hasAnyEmailMatched,
    groupMatches: hasAnyEmailMatched
      ? fileMatches.filter(
          (f) => emailFields.includes(f.field as ColumnKey) && f.matched,
        )
      : [],
  };

  const updated = [...fileMatches];
  updated[groupIndex] = updatedGroup;
  return updated;
}

export function validateColumnsValues(
  fieldKey: ColumnKey,
  values: string[],
): boolean {
  for (const rowValue of values) {
    const validation = validateRowsValuesByField(fieldKey, rowValue);
    if (!validation.valid) return false;
  }
  return true;
}

export function generateRandomString(length: number = 5): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function detectHeaders(firstRow: string[]): string {
  if (!firstRow || firstRow.length === 0) return 'false';

  let matches = 0;

  firstRow.forEach((value) => {
    const trimmedValue = value?.trim() || '';
    const hasMatch = EXPECTED_COLUMN_FIELDS.some((field) =>
      field.labels.some(
        (label) => label.toLowerCase() === trimmedValue.toLowerCase(),
      ),
    );

    if (hasMatch) matches += 1;
  });

  return String(matches >= 2);
}
